import { images } from "@img/imagesMap";
import "./issued-roster-team-groups.css";
import type { ReactNode } from "react";
import { useTeamsQuery } from "@/hooks/roster-craete/useTeamsQuery";
import type { RosterTableLayoutConfig } from "@/components/nurse/rosterColumnHoverLayout";
import { IssuedRosterTeamSkeleton } from "./IssuedRosterTeamSkeleton";
import {
  groupSnapshotTeams,
  type SnapshotTeam,
} from "@/utils/groupSnapshotTeams";
import { groupIssuedRosterTeams } from "../utils/groupIssuedRosterTeams";

export function IssuedRosterTeamGroups<
  T extends { nurse_id: string; team_id?: number | null },
>({
  nurses,
  groupId,
  year,
  month,
  children,
  layoutConfig,
  snapshotTeams = null,
}: {
  snapshotTeams?: readonly SnapshotTeam[] | null;
  nurses: T[];
  groupId: string | null;
  year: number;
  month: number;
  children: (rows: T[]) => ReactNode;
  layoutConfig?: RosterTableLayoutConfig;
}) {
  const {
    data: teams,
    isPending,
    isError,
    refetch,
  } = useTeamsQuery(groupId, year, month, {
    enabled: snapshotTeams === null && Boolean(groupId && year && month),
  });

  if (!groupId) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        병동을 선택해주세요.
      </p>
    );
  }
  if (snapshotTeams === null && isError) {
    return (
      <div
        role="alert"
        className="flex items-center justify-center gap-3 p-6 text-sm"
      >
        <span>팀 정보를 불러오지 못했습니다.</span>
        <button
          type="button"
          className="underline"
          onClick={() => void refetch()}
        >
          다시 시도
        </button>
      </div>
    );
  }
  if (snapshotTeams === null && isPending) {
    return (
      <IssuedRosterTeamSkeleton
        daysInMonth={new Date(year, month, 0).getDate()}
        nurseCount={nurses.length}
        layoutConfig={layoutConfig}
      />
    );
  }

  return (
    <div className="issued-roster-team-groups">
      {(snapshotTeams === null
        ? groupIssuedRosterTeams(teams ?? [], nurses)
        : groupSnapshotTeams(snapshotTeams, nurses)
      ).map((team) => (
        <details
          key={`${groupId}-${year}-${month}-${team.team_id}`}
          open
          className="issued-roster-team"
        >
          <summary className="issued-roster-team-header focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300">
            <span className="issued-roster-team-count">
              <img src={images.user} alt="" className="h-[1em]" />
              {team.nurses.length}
            </span>
            <span className="issued-roster-team-name">{team.team_name}</span>
          </summary>
          {team.nurses.length > 0 ? (
            children(team.nurses)
          ) : (
            <p className="p-4 text-center text-sm text-muted-foreground">
              그룹이 설정된 인원이 없습니다.
            </p>
          )}
        </details>
      ))}
    </div>
  );
}
